"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { Search as SearchIcon, ChevronDown, X, SlidersHorizontal, PanelRight } from "lucide-react";

import { useSearchModal } from "@/contexts/searchContext";
import { SelfAnchoredPopover, useBodyScrollLock } from "@/components/tailwind/ui/anchored-popover";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { useWorkAreaContext } from "@/contexts/workAreaContext";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useIsMobile } from "@/hooks/use-is-mobile";
import {
  recentWorkspace,
  searchWorkspace,
  SearchAbortedError,
  type SearchHit,
  type SearchSort,
} from "@/services-frontend/search/searchClient";
import { getRecentVisits, recordVisit } from "@/lib/recentVisits";
import { buildNoteHref } from "@/utils/noteUrl";
import { SearchFilterChips, dateFilterToWire, type SearchFilters } from "./SearchFilterChips";
import { SearchResultItem } from "./SearchResultItem";
import { SearchSkeletonList, SectionHeaderSkeleton } from "./SearchSkeleton";
import { bucketByDate, BUCKET_ORDER, type Bucket } from "./bucketByDate";
import { cn } from "@/lib/utils";
import { BlockPreview } from "../BlockPreviewCard";

// Persisted across sessions so the palette remembers whether the preview
// pane is open — matches the sticky toggle. Defaults to on.
const PREVIEW_PREF_KEY = "search:showPreview";

function readPreviewPref(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const v = window.localStorage.getItem(PREVIEW_PREF_KEY);
    return v === null ? true : v === "1";
  } catch {
    return true;
  }
}

const SORT_OPTIONS: { value: SearchSort; label: string }[] = [
  { value: "relevance", label: "Best matches" },
  { value: "updatedAt", label: "Last edited" },
  { value: "createdAt", label: "Last created" },
];

type Mode = "recent" | "searching";

/**
 * Toggle a pick in/out of a multi-select filter array. Compares by `id`.
 * Returns `undefined` (not an empty array) when the resulting list is empty
 * so the chip renders in its neutral state and the URL builder omits the
 * param.
 *
 * Used by the in-result-row "click breadcrumb to filter" and "click owner to
 * filter" affordances, so a second click on the same row removes the filter.
 */
function toggleFilterPick<T extends { id: string }>(
  list: T[] | undefined,
  item: T,
): T[] | undefined {
  const current = list ?? [];
  const idx = current.findIndex((x) => x.id === item.id);
  if (idx >= 0) {
    const next = current.filter((_, i) => i !== idx);
    return next.length > 0 ? next : undefined;
  }
  return [...current, item];
}

/**
 * The search palette. Mounted once at the app root; opened
 * via the sidebar button or ⌘K / Ctrl+K.
 *
 * Two modes:
 *  - `recent` (empty query)   → shows pages grouped by Today/Yesterday/…
 *  - `searching` (typed text) → shows a flat list of search hits
 *
 * Race-safety invariant: every keystroke aborts the previous in-flight
 * request via AbortController, so stale responses can never overwrite a
 * newer result set.
 */
export default function SearchModal() {
  const { isOpen, close } = useSearchModal();
  const { currentWorkspace } = useWorkspaceContext();
  const { workAreas } = useWorkAreaContext();
  const router = useRouter();
  const isMobile = useIsMobile();
  useBodyScrollLock(isOpen);

  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<SearchFilters>({ titleOnly: false });
  const [sort, setSort] = useState<SearchSort>("relevance");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  // "visits" = list came from local visit history (flat, no time buckets).
  // "api"    = list came from /api/search/recent (workspace-wide updates,
  //            grouped by Today/Yesterday/…). Used as the fallback for
  //            workspaces with no local history yet.
  const [recentSource, setRecentSource] = useState<"visits" | "api">("visits");
  // Whether the filter chip row under the input is visible. Toggled by
  // the sliders button in the input row — matches the "Filters" affordance.
  const [showFilters, setShowFilters] = useState(true);
  // Whether the right-hand preview pane is shown. Persisted (see readPreviewPref).
  // Deliberately NOT reset when the modal opens — it's a sticky user pref.
  const [showPreview, setShowPreview] = useState(readPreviewPref);

  const inputRef = useRef<HTMLInputElement>(null);
  const resultListRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const debouncedQuery = useDebouncedValue(query, 150);
  const mode: Mode = query.trim() ? "searching" : "recent";
  const workspaceId =
    currentWorkspace?._id ?? (currentWorkspace as { id?: string })?.id ?? "";
  const hasWorkspace = Boolean(workspaceId);

  // workareaId → name lookup. Every hit (visit log, search results, API
  // recent) carries `workareaId`, so we resolve the teamspace crumb at render
  // time instead of baking it into each row's breadcrumb. Pages that live
  // directly under a workArea (no page ancestors) thus still show "[Teamspace]".
  const workAreaNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const wa of workAreas ?? []) {
      const id = wa._id ?? (wa as { id?: string }).id;
      if (id && wa.name) m.set(String(id), wa.name);
    }
    return m;
  }, [workAreas]);

  const resolveWorkArea = useCallback(
    (id: string): { id: string; name: string } | undefined => {
      const name = workAreaNameById.get(id);
      return name ? { id, name } : undefined;
    },
    [workAreaNameById],
  );

  // Show loading immediately on keystroke (before debounce fires) so the
  // skeletons appear as soon as the user types — otherwise the UI sits
  // showing old results for 150ms, which feels stale.
  useEffect(() => {
    if (!isOpen) return;
    if (query.trim() !== debouncedQuery.trim()) setLoading(true);
  }, [query, debouncedQuery, isOpen]);

  // Reset state when the modal opens so a previous session doesn't leak
  // filters or stale results into the next one.
  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setFilters({ titleOnly: false });
    setSort("relevance");
    setHits([]);
    setTotal(0);
    setActiveIndex(0);
    setSortMenuOpen(false);
    setShowFilters(true);
    // Only flip to loading if we'll actually fetch (workspace present).
    setLoading(hasWorkspace);
    window.setTimeout(() => inputRef.current?.focus(), 10);
  }, [isOpen, hasWorkspace]);

  // Fire either a search (non-empty debounced query) or, for empty query,
  // surface the local visit history. ALWAYS scoped to the currently-open
  // workspace — cross-workspace search is disabled by product choice.
  //
  // Empty-query precedence:
  //   1. localStorage visit history for this workspace (flat, visitedAt-desc)
  //   2. /api/search/workspace/:id/recent (date-bucketed) — fallback for users
  //      whose history is empty (new accounts, cleared storage, fresh device)
  useEffect(() => {
    if (!isOpen) return;
    if (!workspaceId) {
      // No workspace selected — nothing to search; show empty state.
      setHits([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    const q = debouncedQuery.trim();

    // Empty query → try local history first; no network needed.
    //
    // EXCEPT when the user has applied a Created-by or In-page filter — the
    // cached visit list can't honour those filters (we never stored owner
    // or breadcrumb metadata), so we have to hand off to the API instead.
    // Otherwise picking a filter chip without typing would silently do
    // nothing.
    const filtersActive =
      !!filters.createdBy ||
      !!filters.parentPage ||
      !!filters.date ||
      !!filters.workArea;
    if (!q && !filtersActive) {
      const visits = getRecentVisits(String(workspaceId));
      if (visits.length > 0) {
        abortRef.current?.abort();
        abortRef.current = null;
        setRecentSource("visits");
        setHits(visits);
        setTotal(visits.length);
        setActiveIndex(0);
        setLoading(false);
        return;
      }
      // No local history — fall through to the API recent fetch.
      setRecentSource("api");
    } else {
      // Either we're searching or we're filtering recent items by API.
      setRecentSource("api");
    }

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);

    const run = async () => {
      try {
        // Flatten the chip-state picks into id arrays for the API. Undefined
        // (rather than empty array) is intentional — searchClient.joinIds
        // would skip an empty list anyway, but using undefined keeps URL
        // params from appearing as `?createdBy=`.
        const createdByIds = filters.createdBy?.map((c) => c.id);
        const parentPageIds = filters.parentPage?.map((p) => p.id);
        const workareaIds = filters.workArea?.map((w) => w.id);
        // Recompute the date range each fetch so a "Today" preset created
        // at 11:59pm doesn't keep filtering against yesterday after midnight.
        const dateRange = dateFilterToWire(filters.date);

        const result = q
          ? await searchWorkspace({
              workspaceId: String(workspaceId),
              query: q,
              titleOnly: filters.titleOnly,
              createdBy: createdByIds,
              parentPageId: parentPageIds,
              workareaId: workareaIds,
              dateRange,
              sort,
              limit: 30,
              signal: ctrl.signal,
            })
          : await recentWorkspace({
              workspaceId: String(workspaceId),
              createdBy: createdByIds,
              parentPageId: parentPageIds,
              workareaId: workareaIds,
              dateRange,
              limit: 40,
              signal: ctrl.signal,
            });

        if (abortRef.current === ctrl) {
          setHits(result.hits);
          setTotal(result.total);
          setActiveIndex(0);
          setLoading(false);
        }
      } catch (err) {
        if (err instanceof SearchAbortedError) return;
        if (abortRef.current === ctrl) {
          setHits([]);
          setTotal(0);
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      // ctrl.abort();
    };
  }, [debouncedQuery, filters, sort, isOpen, workspaceId]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const openHit = useCallback(
    (hit: SearchHit) => {
      close();
      const targetId = hit.type === "content" && hit.parentPageId ? hit.parentPageId : hit.id;
      if (!targetId) return;
      // Record the visit so it surfaces next time the palette opens.
      // For content hits we record the parent page, not the snippet block.
      if (workspaceId) {
        recordVisit(String(workspaceId), {
          id: targetId,
          title: hit.type === "content" ? "" : hit.title,
          icon: hit.type === "content" ? "" : hit.icon,
          type: hit.type === "content" ? "page" : hit.type,
          visibility: hit.visibility === "unknown" ? null : hit.visibility,
          parentPageId: hit.parentPageId,
          workareaId: hit.workareaId,
          // Preserve the ancestor path so the "Recent" bucket renders the
          // breadcrumb for nested items. For content hits the
          // last crumb is the parent page we're recording, so drop it.
          breadcrumb:
            hit.type === "content" ? hit.breadcrumb.slice(0, -1) : hit.breadcrumb,
          updatedAt: hit.updatedAt,
          createdAt: hit.createdAt,
        });
      }
      router.push(buildNoteHref(targetId, hit.type === "content" ? "" : hit.title, hit.type === "database" ? "collection_view" : undefined));
    },
    [close, router, workspaceId],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(hits.length - 1, 0)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const hit = hits[activeIndex];
        if (hit) openHit(hit);
      } else if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    },
    [hits, activeIndex, openHit, close],
  );

  useEffect(() => {
    if (!resultListRef.current) return;
    const el = resultListRef.current.querySelector<HTMLElement>(
      `[data-search-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const formattedTotal = useMemo(() => formatTotal(total), [total]);

  // For the recent (empty-query) view we render bucketed sections.
  //   - Local visit history → a single "Recent" bucket in visit order.
  //   - API fallback        → date buckets (Today/Yesterday/…).
  const buckets: Bucket[] = useMemo(() => {
    if (mode !== "recent") return [];
    if (recentSource === "visits") {
      return hits.length > 0
        ? [{ key: "today" as Bucket["key"], label: "Recent", hits }]
        : [];
    }
    return bucketByDate(hits);
  }, [mode, hits, recentSource]);

  // Flat index → used by arrow-key navigation. In recent mode the buckets
  // are concatenated in BUCKET_ORDER; in searching mode it's just `hits`.
  const flatHits = useMemo(
    () => (mode === "recent" ? buckets.flatMap((b) => b.hits) : hits),
    [mode, buckets, hits],
  );

  // Keep active index valid as the list changes.
  useEffect(() => {
    if (activeIndex >= flatHits.length) setActiveIndex(Math.max(0, flatHits.length - 1));
  }, [flatHits.length, activeIndex]);

  // Persist the preview-pane toggle across sessions.
  useEffect(() => {
    try {
      window.localStorage.setItem(PREVIEW_PREF_KEY, showPreview ? "1" : "0");
    } catch {
      /* private mode / storage disabled — non-fatal */
    }
  }, [showPreview]);

  // The page whose preview the side pane shows: the currently-focused hit,
  // resolved to its parent page for content matches (mirrors openHit's
  // targetId). Debounced so holding ↓ through results doesn't fire a block
  // fetch per step.
  const activeHit = flatHits[activeIndex];
  const activePreviewId = activeHit
    ? activeHit.type === "content" && activeHit.parentPageId
      ? activeHit.parentPageId
      : activeHit.id
    : null;
  const debouncedPreviewId = useDebouncedValue(activePreviewId, 120);
  // The pane only makes sense with horizontal room, so it's desktop-only.
  const previewOpen = showPreview && !isMobile;

  if (!isOpen) return null;

  return createPortal(
    // Centered floating card on desktop; bottom sheet pinned to the viewport
    // bottom on mobile so it slides up over the on-screen keyboard nicely.
    <div
      className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center bg-black/50 dark:bg-black/70"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        onKeyDown={onKeyDown}
        // Width matches a quick-find palette (~640px) on desktop;
        // widens to fit the preview pane when it's open. Full-width on mobile.
        // Only the top corners round on mobile since the sheet sits flush
        // against the viewport bottom edge.
        className={cn(
          "relative z-[1] w-full rounded-t-2xl md:rounded-2xl overflow-hidden bg-modal shadow-2xl border border-component grid pb-[env(safe-area-inset-bottom)] md:pb-0",
          previewOpen
            ? "md:w-[min(980px,calc(100vw-32px))]"
            : "md:w-[min(640px,calc(100vw-32px))]",
        )}
        style={
          isMobile
            ? { height: "85dvh", gridTemplateRows: "auto auto auto minmax(0,1fr) auto" }
            : { maxHeight: "82vh", height: "calc(100vh - 140px)", gridTemplateRows: "auto auto minmax(0,1fr) auto" }
        }
        role="dialog"
        aria-modal="true"
        aria-label="Search"
      >
        <h2 className="sr-only">Search</h2>

        {/* Mobile-only drag handle pill — visual affordance signalling the
            modal is a bottom sheet. */}
        {isMobile && (
          <div
            aria-hidden="true"
            className="mx-auto mt-2 mb-1 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30"
          />
        )}

        {/* Input row */}
        <div className="flex items-center gap-2 px-6 py-3">
          <SearchIcon className="h-5 w-5 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              hasWorkspace
                ? `Search ${currentWorkspace?.name || "this workspace"}…`
                : "Open a workspace to search…"
            }
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            maxLength={500}
            autoComplete="off"
            spellCheck={false}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={flatHits.length > 0}
            aria-controls="search-results-listbox"
            disabled={!hasWorkspace}
          />
          {query ? (
            <button
              type="button"
              aria-label="Clear query"
              title="Clear query"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              className="rounded-full p-1 hover-component "
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
          {/*
            Filter toggle. Stays visible at all times (even when no chip is
            active) so users can re-open the row after dismissing it. The
            blue tint signals the row is currently expanded, matching the
            active-state styling on the chips themselves.
          */}
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            aria-label={showFilters ? "Hide filters" : "Show filters"}
            title={showFilters ? "Hide filters" : "Show filters"}
            aria-pressed={showFilters}
            className={cn(
              "rounded-full p-1.5 transition-colors",
              showFilters
                ? "bg-blue-50 dark:bg-blue-900/20 text-blue-500 dark:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                : "text-muted-foreground hover-component ",
            )}
          >
            <SlidersHorizontal className="h-4 w-4" />
          </button>
          {/*
            Preview-pane toggle. Desktop only — the pane needs horizontal
            room a phone doesn't have. Blue tint signals the pane is open,
            matching the filter button's active styling.
          */}
          {!isMobile ? (
            <button
              type="button"
              onClick={() => setShowPreview((v) => !v)}
              aria-label={showPreview ? "Hide preview" : "Show preview"}
              aria-pressed={showPreview}
              title={showPreview ? "Hide preview" : "Show preview"}
              className={cn(
                "rounded-full p-1.5 transition-colors",
                showPreview
                  ? "bg-blue-50 dark:bg-blue-900/20 text-blue-500 dark:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                  : "text-muted-foreground hover-component ",
              )}
            >
              <PanelRight className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {/*
          Filter chips. Always render the slot so the parent grid keeps its
          row count (header / chips / body / footer). When hidden, the
          wrapper sits at 0 height — collapsing the border too, otherwise
          you'd see a stray divider line under the input row.
        */}
        <div className={showFilters ? "" : ""}>
          {showFilters ? (
            <SearchFilterChips
              value={filters}
              onChange={setFilters}
              workspaceId={workspaceId ? String(workspaceId) : undefined}
            />
          ) : null}
        </div>

        {/* Body — result list (left) + optional preview pane (right). */}
        <div className="flex min-h-0 overflow-hidden">
          <div
            ref={resultListRef}
            id="search-results-listbox"
            role="listbox"
            className={cn(
              "min-h-0 overflow-y-auto clean-scrollbar px-4 py-4",
              previewOpen ? "flex-1 min-w-0" : "w-full",
            )}
          >
            {/* SEARCHING MODE */}
            {mode === "searching" ? (
              <SearchingView
                loading={loading}
                hits={hits}
                query={query}
                total={total}
                formattedTotal={formattedTotal}
                sort={sort}
                onSortChange={setSort}
                sortMenuOpen={sortMenuOpen}
                setSortMenuOpen={setSortMenuOpen}
                showBodyPreview={!filters.titleOnly}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                openHit={openHit}
                onBreadcrumbClick={(crumb) =>
                  setFilters((f) => ({
                    ...f,
                    parentPage: toggleFilterPick(f.parentPage, crumb),
                  }))
                }
                onOwnerClick={(owner) =>
                  setFilters((f) => ({
                    ...f,
                    createdBy: toggleFilterPick(f.createdBy, owner),
                  }))
                }
                resolveWorkArea={resolveWorkArea}
                onWorkAreaClick={(wa) =>
                  setFilters((f) => ({
                    ...f,
                    workArea: toggleFilterPick(f.workArea, wa),
                  }))
                }
              />
            ) : (
              /* RECENT (empty-query) MODE */
              <RecentView
                loading={loading}
                buckets={buckets}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                openHit={openHit}
                onBreadcrumbClick={(crumb) =>
                  setFilters((f) => ({
                    ...f,
                    parentPage: toggleFilterPick(f.parentPage, crumb),
                  }))
                }
                onOwnerClick={(owner) =>
                  setFilters((f) => ({
                    ...f,
                    createdBy: toggleFilterPick(f.createdBy, owner),
                  }))
                }
                resolveWorkArea={resolveWorkArea}
                onWorkAreaClick={(wa) =>
                  setFilters((f) => ({
                    ...f,
                    workArea: toggleFilterPick(f.workArea, wa),
                  }))
                }
              />
            )}
          </div>

          {/* Preview pane — mirrors the editor/gallery hover card, shown as a
              persistent panel keyed to the focused result. */}
          {previewOpen ? (
            <aside className="w-[380px] shrink-0 min-h-0 overflow-hidden border-component bg-modal p-3">
              <BlockPreview
                blockId={debouncedPreviewId}
                variant="panel"
                onOpen={activeHit ? () => openHit(activeHit) : undefined}
              />
            </aside>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-component px-4 py-2 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <ShortcutHint keys={["↑", "↓"]} label="Navigate" />
            <ShortcutHint keys={["↵"]} label="Open" />
            {/* <ShortcutHint keys={["Esc"]} label="Close" /> */}
          </div>
          <div className="opacity-70">{loading ? "Loading…" : null}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* -------------------------------------------------------------------------- */
/* Subviews                                                                   */
/* -------------------------------------------------------------------------- */

interface SearchingViewProps {
  loading: boolean;
  hits: SearchHit[];
  query: string;
  total: number;
  formattedTotal: string;
  sort: SearchSort;
  onSortChange: (s: SearchSort) => void;
  sortMenuOpen: boolean;
  setSortMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showBodyPreview: boolean;
  activeIndex: number;
  setActiveIndex: (n: number) => void;
  openHit: (h: SearchHit) => void;
  onBreadcrumbClick: (crumb: { id: string; title: string }) => void;
  onOwnerClick: (owner: { id: string; name: string }) => void;
  resolveWorkArea: (id: string) => { id: string; name: string } | undefined;
  onWorkAreaClick: (wa: { id: string; name: string }) => void;
}

function SearchingView(props: SearchingViewProps) {
  const {
    loading,
    hits,
    query,
    total,
    formattedTotal,
    sort,
    onSortChange,
    sortMenuOpen,
    setSortMenuOpen,
    showBodyPreview,
    activeIndex,
    setActiveIndex,
    openHit,
    onBreadcrumbClick,
    onOwnerClick,
    resolveWorkArea,
    onWorkAreaClick,
  } = props;

  if (loading && hits.length === 0) {
    // First-paint loading — header with spinner, rows as skeleton.
    return (
      <div className="flex flex-col">
        <div className="flex items-center justify-between px-3 pb-2 pt-1">
          <div className="text-xs font-medium text-muted-foreground">Search results</div>
          <SectionHeaderSkeleton />
        </div>
        <SearchSkeletonList count={6} />
      </div>
    );
  }

  if (!loading && hits.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-sm text-muted-foreground">
        No results for <span className="font-medium">&ldquo;{query}&rdquo;</span>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between px-3 pb-2 pt-1">
        <div className="text-xs font-medium text-muted-foreground">
          Search results ({formattedTotal})
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setSortMenuOpen((v) => !v)}
            className="flex items-center gap-1 rounded text-xs text-muted-foreground hover:text-foreground"
          >
            {SORT_OPTIONS.find((o) => o.value === sort)?.label ?? "Best matches"}
            <ChevronDown className="h-3 w-3" />
          </button>
          {sortMenuOpen ? (
            <SelfAnchoredPopover
              placement="bottom-end"
              onClose={() => setSortMenuOpen(false)}
              className="min-w-[150px] overflow-hidden rounded-lg border border-component bg-modal shadow-2xl"
            >
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onSortChange(opt.value);
                    setSortMenuOpen(false);
                  }}
                  className={cn(
                    "block w-full px-3 py-1.5 text-left text-xs hover-component ",
                    sort === opt.value && "font-medium text-foreground",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </SelfAnchoredPopover>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col">
        {hits.map((hit, idx) => (
          <div key={hit.id} data-search-index={idx}>
            <SearchResultItem
              hit={hit}
              active={idx === activeIndex}
              showBodyPreview={showBodyPreview}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => openHit(hit)}
              onBreadcrumbClick={onBreadcrumbClick}
              onOwnerClick={onOwnerClick}
              resolveWorkArea={resolveWorkArea}
              onWorkAreaClick={onWorkAreaClick}
            />
          </div>
        ))}
      </div>
    </>
  );
}

interface RecentViewProps {
  loading: boolean;
  buckets: Bucket[];
  activeIndex: number;
  setActiveIndex: (n: number) => void;
  openHit: (h: SearchHit) => void;
  onBreadcrumbClick: (crumb: { id: string; title: string }) => void;
  onOwnerClick: (owner: { id: string; name: string }) => void;
  resolveWorkArea: (id: string) => { id: string; name: string } | undefined;
  onWorkAreaClick: (wa: { id: string; name: string }) => void;
}

function RecentView(props: RecentViewProps) {
  const {
    loading,
    buckets,
    activeIndex,
    setActiveIndex,
    openHit,
    onBreadcrumbClick,
    onOwnerClick,
    resolveWorkArea,
    onWorkAreaClick,
  } = props;

  // First-paint (no data yet) → render placeholder section headers with
  // skeleton rows so the layout matches the screenshot: headers
  // stay visible, rows pulse.
  if (loading && buckets.length === 0) {
    return (
      <div className="flex flex-col">
        {BUCKET_ORDER.slice(0, 3).map(({ key, label }) => (
          <div key={key} className="mb-2">
            <div className="flex items-center justify-between px-3 pb-1 pt-1">
              <div className="text-xs font-medium text-muted-foreground">{label}</div>
              <SectionHeaderSkeleton />
            </div>
            <SearchSkeletonList count={2} />
          </div>
        ))}
      </div>
    );
  }

  if (!loading && buckets.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-sm text-muted-foreground">
        Start typing to search, or create some pages first.
      </div>
    );
  }

  // Flat index counter so arrow-key nav can target items across sections.
  let running = 0;

  return (
    <div className="flex flex-col">
      {buckets.map((bucket) => {
        const start = running;
        running += bucket.hits.length;
        return (
          <div key={bucket.key} className="mb-2">
            <div className="flex items-center justify-between px-3 pb-1 pt-1">
              <div className="text-xs font-medium text-muted-foreground">{bucket.label}</div>
              {/* Reserved for per-section counts / actions (matches the reserved space). */}
              <div className="text-[10px] text-muted-foreground/70">{bucket.hits.length}</div>
            </div>
            {bucket.hits.map((hit, i) => {
              const flatIdx = start + i;
              return (
                <div key={hit.id} data-search-index={flatIdx}>
                  <SearchResultItem
                    hit={hit}
                    active={flatIdx === activeIndex}
                    showBodyPreview={false}
                    onMouseEnter={() => setActiveIndex(flatIdx)}
                    onClick={() => openHit(hit)}
                    onBreadcrumbClick={onBreadcrumbClick}
                    onOwnerClick={onOwnerClick}
                    resolveWorkArea={resolveWorkArea}
                    onWorkAreaClick={onWorkAreaClick}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function ShortcutHint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <div className="flex items-center gap-1">
      <span className="flex items-center gap-0.5">
        {keys.map((k) => (
          <kbd
            key={k}
            className="rounded border border-component bg-gray-100 dark:bg-[rgb(42,42,42)] px-1.5 py-0.5 text-[10px] font-medium"
          >
            {k}
          </kbd>
        ))}
      </span>
      <span>{label}</span>
    </div>
  );
}

function formatTotal(n: number): string {
  if (n <= 0) return "0";
  if (n >= 100) return "100+";
  return String(n);
}
